import { AlertTriangle, ExternalLink, Terminal, Wrench } from 'lucide-react'
import ModuleLayout from '../components/ModuleLayout'
import StepByStep from '../components/StepByStep'
import CopyBlock from '../components/CopyBlock'
import SheetSubmission from '../components/SheetSubmission'
import {
  PRACTICE_TAB, PRACTICE_HEADERS, STARTING_ROWS, EDITED_ROWS,
} from '../data/scriptPractice'
import { MODULE_22_CHECKS } from '../data/scriptChecks'

/**
 * Module 1 of the Apps Script course (id 22).
 *
 * Nothing here needs Claude — that is the point of the course being separate.
 * The trainee is given the script rather than asked to write one, because the
 * skill being taught is "code arrived, now what do I do with it", which is
 * exactly the position anyone is in the first time a colleague or an agent
 * hands them a script.
 */

const HELLO = `function helloWorld() {
  SpreadsheetApp.getActiveSheet().getRange('A1').setValue('Hello from Apps Script');
}`

const SCRIPT = `function buildPractice() {
  const ROWS = ${STARTING_ROWS};

  const sheet = SpreadsheetApp.getActiveSpreadsheet();
  let tab = sheet.getSheetByName('${PRACTICE_TAB}');
  if (!tab) tab = sheet.insertSheet('${PRACTICE_TAB}');
  tab.clear();

  const headings = [${PRACTICE_HEADERS.map((h) => `'${h}'`).join(', ')}];
  const data = [];
  const formulas = [];

  for (let i = 1; i <= ROWS; i++) {
    const units = 1 + ((i * 7) % 40);
    const price = Math.round((5 + ((i * 13) % 95)) * 100) / 100;
    const row = i + 1;

    data.push(['Day ' + i, units, price]);
    formulas.push(['=B' + row + '*C' + row]);
  }

  // Values for the columns that hold facts...
  tab.getRange(1, 1, 1, headings.length).setValues([headings]);
  tab.getRange(2, 1, ROWS, 3).setValues(data);

  // ...and a formula for the column that holds a calculation.
  tab.getRange(2, 4, ROWS, 1).setFormulas(formulas);

  tab.getRange(1, 1, 1, headings.length).setFontWeight('bold');
  tab.setFrozenRows(1);
}`

function SectionWhy() {
  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">Somebody hands you a script</h2>
      <p className="mb-4 text-slate-600">
        Sooner or later someone sends you code for a spreadsheet — a colleague, a vendor, or an AI
        that just wrote it for you. The useful skill isn't writing it. It's knowing where it goes,
        what the scary permission box is asking, how to run it, and what to do when it goes red.
      </p>
      <p className="mb-4 text-slate-600">
        So you're not going to write anything in this module. You're going to take a script that
        already exists, run it, and then change one line of it — which is the honest version of what
        most people do with code for the rest of their careers.
      </p>
      <div className="rounded-xl border-2 border-rose-200 bg-rose-50 p-5">
        <h3 className="font-semibold text-rose-900">What Apps Script actually is</h3>
        <p className="mt-1 text-sm text-rose-800">
          JavaScript that lives inside your spreadsheet and runs on Google's servers, with
          permission to touch that spreadsheet. Not installed, not on your laptop, nothing to set
          up — it travels with the file. Anyone you share the sheet with as an editor gets the
          script too, which is a feature and occasionally a nasty surprise.
        </p>
      </div>
    </section>
  )
}

function SectionHelloWorld() {
  const steps = [
    {
      title: 'Make a spreadsheet to practise in',
      content: (
        <p>
          A fresh one at{' '}
          <a href="https://sheets.new" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
            sheets.new <ExternalLink className="h-3 w-3" />
          </a>
          . Call it something like <strong>Apps Script practice</strong>. You'll use this same sheet
          in the next module.
        </p>
      ),
    },
    {
      title: 'Open the editor',
      content: (
        <div className="space-y-2">
          <p>
            In the spreadsheet menu: <strong>Extensions ▸ Apps Script</strong>. A new tab opens with
            a file called <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">Code.gs</code>{' '}
            containing an empty <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">myFunction</code>.
          </p>
          <p className="text-sm text-slate-500">
            That editor is bound to this one spreadsheet. Nothing you do in it affects any other
            file.
          </p>
        </div>
      ),
    },
    {
      title: 'Paste in the smallest script there is',
      content: (
        <div className="space-y-3">
          <p>
            Delete the empty <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">myFunction</code>{' '}
            that's there, paste this in its place, and press the save icon (or Ctrl/Cmd + S).
          </p>
          <CopyBlock text={HELLO} label="Copy script" />
          <p className="text-sm text-slate-500">
            One line of actual instruction, and you can read it left to right: take this
            spreadsheet's active sheet, take cell A1, set its value. That is the whole shape of
            Apps Script — get hold of something, then do something to it.
          </p>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
            <strong className="text-slate-800">Two lines worth stopping on</strong>, near the
            bottom — they are the reason this script is worth reading rather than just running:
            <pre className="mt-2 overflow-x-auto rounded border border-slate-200 bg-white p-2 text-xs">
              <code>{`tab.getRange(2, 1, ROWS, 3).setValues(data);
tab.getRange(2, 4, ROWS, 1).setFormulas(formulas);`}</code>
            </pre>
            <p className="mt-2">
              <strong className="text-slate-800">setValues</strong> writes what you hand it: Day,
              Units and Price are facts, and a fact is just a value.{' '}
              <strong className="text-slate-800">setFormulas</strong> writes something the
              spreadsheet will keep working out for itself — Total is a calculation, so the cell
              gets <code className="rounded bg-white px-1 py-0.5 text-xs">=B2*C2</code> rather than
              the answer.
            </p>
            <p className="mt-2">
              The script could easily have multiplied those two numbers itself and written 428.4.
              The sheet would look identical — until somebody changes a price, at which point a
              formula recalculates and a number sits there being wrong. When a column is a
              calculation, write the calculation.
            </p>
          </div>
        </div>
      ),
      highlight:
        'Reading code you were given before running it is the whole safety story. If you cannot tell what it does, that is a reason to ask, not to click Run.',
    },
    {
      title: 'Run it, and read the permission screen',
      content: (
        <div className="space-y-2">
          <p>
            Press <strong>Run</strong> — <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">helloWorld</code>{' '}
            is the only function in the file, so it is already selected. Google asks for permission
            the first time, in two steps:
          </p>
          <ol className="ml-5 list-decimal space-y-1 text-sm text-slate-600">
            <li>
              <strong>Authorization required</strong> — click <strong>Review permissions</strong>.
            </li>
            <li>
              <strong>“Untitled project wants access to your Google Account”</strong> — read what it
              is asking for, then click <strong>Continue</strong>.
            </li>
          </ol>
          <p className="text-sm text-slate-500">
            On a Finder account that's the whole flow. If you ever see a warning that the app{' '}
            <em>isn't verified</em>, with an <strong>Advanced</strong> link, you're signed in with a
            personal Google account rather than your work one — check the account in the top right
            before going any further.
          </p>
        </div>
      ),
      highlight:
        'Switch back to the spreadsheet. A1 says "Hello from Apps Script" — written by a program, in a file you own, from a tab in your browser.',
    },
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">Hello, world</h2>
      <p className="mb-5 text-slate-600">
        The smallest thing that proves the whole chain works: a script you can read in one glance,
        a permission dialog, and a cell that changes. Everything after this is the same loop with
        more lines in it.
      </p>
      <StepByStep steps={steps} />
    </section>
  )
}

function SectionBuildTable() {
  const steps = [
    {
      title: 'Paste in something that actually does a job',
      content: (
        <div className="space-y-3">
          <p>
            Back in the editor. Leave{' '}
            <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">helloWorld</code> where it is
            — a file holds as many functions as you like — and paste this underneath it.
          </p>
          <CopyBlock text={SCRIPT} label="Copy script" />
          <p className="text-sm text-slate-500">
            About twenty lines, and you can still read what it does: find or create a tab, clear
            it, build a list of rows, write them, embolden the headings. You do not need to follow
            every line — you need to be able to see it is not doing anything alarming.
          </p>
        </div>
      ),
      highlight:
        'No permission dialog this time. You granted access on the first run and it covers everything in this project — which is worth knowing: approving one script approves the next one too.',
    },
    {
      title: 'Run it — but pick the right function',
      content: (
        <div className="space-y-2">
          <p>
            There are two functions in the file now, so the dropdown at the top matters. Choose{' '}
            <strong>buildPractice</strong>, then press <strong>Run</strong>.
          </p>
          <p className="text-sm text-slate-500">
            Running the wrong one is the commonest confusion in this editor: it reports success,
            nothing you were expecting happens, and there is no error because nothing went wrong.
          </p>
        </div>
      ),
      highlight: `Switch back to the spreadsheet: a "${PRACTICE_TAB}" tab with ${STARTING_ROWS} rows of data you never typed.`,
    },
    {
      title: `Now change one number — make it ${EDITED_ROWS} rows`,
      content: (
        <div className="space-y-3">
          <p>
            Back in the editor, look at the <strong>second line of the function</strong> — the one
            directly under{' '}
            <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">function buildPractice() {'{'}</code>.
            It reads:
          </p>
          <pre className="overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
            <code>{`  const ROWS = ${STARTING_ROWS};`}</code>
          </pre>
          <p>
            Click into it and change the{' '}
            <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">{STARTING_ROWS}</code> to a{' '}
            <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">{EDITED_ROWS}</code>, so it
            reads:
          </p>
          <pre className="overflow-x-auto rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
            <code>{`  const ROWS = ${EDITED_ROWS};`}</code>
          </pre>
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <strong>Save before you run it.</strong> Ctrl + S, or Cmd + S on a Mac. The editor runs
            the last <em>saved</em> version, so a 25 sitting on screen unsaved still runs as 10 —
            and the sheet comes back with 10 rows and no error to explain why.
          </p>
          <p className="text-sm text-slate-500">
            Edit the number where it sits. Don't paste in a second{' '}
            <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">const ROWS</code> line —
            two of them in one function is an error, not an override.
          </p>
          <p>Then press <strong>Run</strong> again. The tab is rebuilt with {EDITED_ROWS} rows.</p>
        </div>
      ),
      highlight:
        "That is the moment worth having. You just changed what a program does without knowing JavaScript — because someone named the thing you needed to change clearly.",
    },
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">Now something worth running</h2>
      <p className="mb-5 text-slate-600">
        One cell is a demonstration. This one builds a table you would otherwise have spent twenty
        minutes typing — and then you are going to change what it produces without knowing any
        JavaScript.
      </p>
      <StepByStep steps={steps} />
    </section>
  )
}

function SectionTheSidebar() {
  const items = [
    {
      icon: 'ⓘ',
      name: 'Overview',
      what: 'A summary page: what this project is called, when it last ran, and how many runs failed recently.',
      when: "Rarely, on purpose. It's the page to glance at if someone asks whether a script is still being used.",
    },
    {
      icon: '< >',
      name: 'Editor',
      what: 'The code itself. Files down the left (Code.gs to start with), the editor in the middle, and along the top the function dropdown, Run, and Debug.',
      when: 'Almost always. This is where you paste, save (Ctrl/Cmd + S) and run. Everything in this course happens here.',
    },
    {
      icon: '↺',
      name: 'Project History',
      what: "Snapshots of the code over time, with what changed. It's a version history you never have to set up.",
      when: "When something worked yesterday and doesn't now. Compare against an earlier version rather than trying to remember what you altered.",
    },
    {
      icon: '⏰',
      name: 'Triggers',
      what: 'The schedule. Every automatic run — time-driven, on-open, on-edit — is listed here, with which function it calls and who owns it.',
      when: "Setting up the daily run in the next module, and any time you inherit a sheet and want to know what it does when nobody's looking.",
    },
    {
      icon: '☰',
      name: 'Executions',
      what: 'A log of every run: when, started by what (you, a trigger, a button), how long it took, and Completed or Failed. Click any row to expand it and you get that run\'s full output — the error, and anything the script logged while it was running.',
      when: "The first place to look when an automated job has quietly stopped working. A trigger that has been failing for weeks looks exactly like one with nothing to do, and this is the page that tells them apart. It is also the ONLY place to see output from a run you weren't there for: the log at the bottom of the Editor shows the run you just did and nothing else.",
    },
    {
      icon: '⚙',
      name: 'Project Settings',
      what: "Time zone, the script's ID, whether to show the appsscript.json manifest file, and script properties — a place to keep settings out of the code.",
      when: 'Setting the time zone before you schedule anything, so 7am means 7am here. Otherwise seldom.',
    },
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">The six things down the left</h2>
      <p className="mb-5 text-slate-600">
        The sidebar looks like more than it is. You'll live in <strong>Editor</strong>, visit{' '}
        <strong>Triggers</strong> and <strong>Executions</strong> when something runs on its own, and
        open the other three about once a year.
      </p>
      <dl className="space-y-3">
        {items.map(({ icon, name, what, when }) => (
          <div key={name} className="rounded-lg border border-slate-200 bg-white p-4">
            <dt className="flex items-center gap-2.5">
              <span className="inline-flex h-7 w-9 shrink-0 items-center justify-center rounded border border-slate-200 bg-slate-50 font-mono text-xs text-slate-500">
                {icon}
              </span>
              <span className="font-semibold text-slate-800">{name}</span>
            </dt>
            <dd className="mt-2 space-y-1 pl-[3.1rem] text-sm">
              <p className="text-slate-600">{what}</p>
              <p className="text-slate-500">
                <span className="font-medium text-slate-600">When you'd open it: </span>
                {when}
              </p>
            </dd>
          </div>
        ))}
      </dl>
      <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-800">
          Leaving yourself a note in the log
        </h3>
        <p className="mt-1 text-sm text-slate-600">
          Anything a script writes with{' '}
          <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">console.log('...')</code> is
          kept with that run in Executions — so a line like{' '}
          <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">console.log('wrote ' + rows.length + ' rows')</code>{' '}
          tells you weeks later what actually happened at 7am on a morning nobody was watching. It
          is the cheapest debugging there is, and the only kind available for a script that runs
          while you are asleep.
        </p>
      </div>

      <p className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        <strong className="text-slate-800">Along the top, in the Editor:</strong> the project name
        (click it to rename), then <strong>Run</strong>, <strong>Debug</strong>, and the function
        dropdown. Run does whichever function is showing in that dropdown — the commonest confusion
        in here is running the wrong one and wondering why nothing changed.
      </p>
    </section>
  )
}

function SectionWhatYouJustAgreedTo() {
  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">Read that permission screen again</h2>
      <p className="mb-4 text-slate-600">
        It said the project could <strong>“See, edit, create, and delete all your Google Sheets
        spreadsheets.”</strong> Not this spreadsheet. <em>All</em> of them.
      </p>
      <p className="mb-4 text-slate-600">
        That is how Apps Script permissions work: they are granted per scope, not per file. Your
        script only touches this sheet because that is what the code says — not because Google is
        stopping it from touching anything else.
      </p>
      <div className="space-y-3">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <h3 className="text-sm font-semibold text-amber-900">
            Which is why you read a script before you run it
          </h3>
          <p className="mt-1 text-sm text-amber-800">
            You wrote none of this one, and you approved it access to every spreadsheet you can
            open — including anything shared with you. For a fifteen-line script you have read,
            that's fine. For one someone sent you, or one an agent produced while you were half
            watching, the reading is the control. There isn't another one.
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-800">Name your project</h3>
          <p className="mt-1 text-sm text-slate-600">
            The screen said <strong>“Untitled project”</strong>, because that's what a new script is
            called. Click the name at the top left of the editor and give it a real one. When a
            colleague sees that dialog on a sheet you shared, “Untitled project wants access to your
            Google Account” is not a confidence-building sentence.
          </p>
        </div>
      </div>
    </section>
  )
}

function SectionWhenItGoesRed() {
  const errors = [
    {
      e: 'TypeError: Cannot read properties of null',
      m: 'Something it looked for was not there — usually a tab name that does not match. Check the spelling against the tab at the bottom of your sheet.',
    },
    {
      e: 'Exception: You do not have permission to call…',
      m: 'You cancelled the authorisation dialog, or you are signed into the editor as a different Google account than the sheet. Run it again and approve.',
    },
    {
      e: 'ReferenceError: buildPractice is not defined',
      m: 'The file was not saved, or the function name in the dropdown does not match the one in the code.',
    },
    {
      e: 'Exception: The number of rows in the data does not match',
      m: 'setValues needs a rectangle: every row the same length. It usually means a row was built with a column missing.',
    },
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">When it goes red</h2>
      <p className="mb-4 text-slate-600">
        The execution log appears under the editor and tells you the line number. Errors are normal
        and specific — they're not a verdict on you, they're the machine saying which line it got
        stuck on. Four you'll actually meet:
      </p>
      <dl className="space-y-3">
        {errors.map(({ e, m }) => (
          <div key={e} className="rounded-lg border border-slate-200 bg-white p-4">
            <dt className="flex items-start gap-2 font-mono text-xs font-semibold text-rose-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" />
              {e}
            </dt>
            <dd className="mt-1.5 pl-6 text-sm text-slate-600">{m}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-5">
        <div className="flex items-start gap-3">
          <Wrench className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />
          <div>
            <h3 className="text-sm font-semibold text-slate-800">The move that fixes most of them</h3>
            <p className="mt-1 text-sm text-slate-600">
              Copy the whole red message — all of it, including the line number — and paste it
              somewhere that can read it: a colleague, Gemini, Claude. "It didn't work" gets you
              nowhere; the exact text of the error usually gets you the answer in one go.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

function SectionTroubleshooting() {
  const problems = [
    {
      p: 'There is no Extensions menu',
      s: "You're in a spreadsheet you can only view. Make your own at sheets.new, or ask the owner for edit access.",
    },
    {
      p: 'The permission screen keeps reappearing',
      s: 'You are signed into the Apps Script editor as one Google account and the spreadsheet as another. Check the avatar in the top right of both tabs.',
    },
    {
      p: 'The Run button is greyed out',
      s: 'The file has unsaved changes, or no function is selected in the dropdown next to it. Save, then pick buildPractice.',
    },
    {
      p: 'It ran, but nothing happened in the sheet',
      s: 'Look for a new tab along the bottom — it may be off to the right. If the log says the run finished with no errors, the data exists somewhere.',
    },
    {
      p: 'I pasted it under the existing myFunction and now it complains',
      s: 'Delete everything in the file first and paste again. Two half-finished functions in one file is the most common cause of a confusing error here.',
    },
  ]

  return (
    <section>
      <h2 className="mb-3 text-2xl font-bold text-slate-800">If you get stuck</h2>
      <dl className="space-y-3">
        {problems.map(({ p, s }) => (
          <div key={p} className="rounded-lg border border-slate-200 bg-white p-4">
            <dt className="flex items-start gap-2 text-sm font-semibold text-slate-800">
              <Terminal className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              {p}
            </dt>
            <dd className="mt-1.5 pl-6 text-sm text-slate-600">{s}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

export default function ModuleFirstAppsScript() {
  return (
    <ModuleLayout moduleId={22}>
      <div className="space-y-12">
        <SectionWhy />
        <SectionHelloWorld />
        <SectionWhatYouJustAgreedTo />
        <SectionTheSidebar />
        <SectionBuildTable />
        <SectionWhenItGoesRed />
        <SectionTroubleshooting />
        <SheetSubmission
          moduleId={22}
          checks={MODULE_22_CHECKS}
          heading="Check your practice sheet"
          intro="Paste the link to the spreadsheet you practised in. This checks what the script produced — including whether your edit took effect."
        />
      </div>
    </ModuleLayout>
  )
}
